import { formatRankLabel, getSegmentRadius } from "../utils/rankingResultsUtils";
import { pollColorPalette } from "../utils/pollColorPalette";

interface BaseVisualizationProps {
  optionId: string | undefined;
  counts: number[];
  bucketLabels: string[];
  rankColors: string[];
  overflowColor: string;
  averageRank: number;
  showAverageMarker?: boolean;
}

export interface StackedDistributionProps extends BaseVisualizationProps {
  userRank: number | undefined;
  userRankBucketIndex: number | null;
  baseTotal: number;
  averagePosition: number;
}

export const RankingStackedDistribution = ({
  optionId,
  counts,
  bucketLabels,
  rankColors,
  overflowColor,
  userRank,
  userRankBucketIndex,
  baseTotal,
  averageRank,
  averagePosition,
  showAverageMarker = true,
}: StackedDistributionProps) => {
  let userRankOverlayPosition: number | null = null;
  const markers: {
    key: string;
    label: string;
    position: number;
    color: string;
    labelBg: string;
    labelText: string;
  }[] = [];

  if (showAverageMarker && averageRank) {
    markers.push({
      key: "average",
      label: `Avg ${averageRank.toFixed(1)}`,
      position: averagePosition,
      color: pollColorPalette.averageIndicator,
      labelBg: "#ffffff",
      labelText: pollColorPalette.averageIndicator,
    });
  }

  const clampedMarkerPositions = markers.map((marker) => Math.min(100, Math.max(0, marker.position)));
  const markerOverlapThreshold = 4;
  const markerBaseBottom = 24;
  const markerStaggerOffset = 26;
  const markerBottomOffsets = clampedMarkerPositions.map((position, index) => {
    const overlaps = clampedMarkerPositions
      .slice(0, index)
      .some((prev) => Math.abs(prev - position) < markerOverlapThreshold);
    return overlaps ? markerBaseBottom + markerStaggerOffset : markerBaseBottom;
  });

  const segments = (() => {
    let cumulativeWidth = 0;
    return counts.map((count, rankIndex) => {
      const percentage = Math.min(100, (count / baseTotal) * 100);
      const isUserRank = userRankBucketIndex === rankIndex;
      const width = Math.max(percentage, isUserRank ? 2 : 0);
      if (width <= 0) return null;

      const segmentStart = cumulativeWidth;
      cumulativeWidth += width;

      const labelFitsInside = width >= 15;
      const labelPosition = Math.min(100, Math.max(0, segmentStart + width / 2));
      const label = bucketLabels[rankIndex];

      if (isUserRank && !labelFitsInside) {
        userRankOverlayPosition = labelPosition;
      }

      return (
        <div
          key={`${optionId}-rank-${rankIndex}`}
          className="relative h-full"
          style={{ width: `${width}%` }}
          title={`${label} choice: ${percentage.toFixed(1)}%`}
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundColor: rankColors[rankIndex] || overflowColor,
              borderRadius: getSegmentRadius(rankIndex, counts.length),
            }}
          />
          {isUserRank && (
            <div
              className="pointer-events-none absolute inset-0 border-[2px] border-black"
              style={{ borderRadius: getSegmentRadius(rankIndex, counts.length) }}
            />
          )}
          {isUserRank && labelFitsInside && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-black">
              <span className="px-1.5 py-0.5 rounded bg-white/85">{userRank}</span>
            </div>
          )}
        </div>
      );
    });
  })();

  return (
    <div className="relative w-full">
      <div className="relative w-full h-8 rounded overflow-hidden bg-muted/60">
        <div className="flex h-full">{segments}</div>
      </div>
      {userRankOverlayPosition !== null && (
        <div
          className="pointer-events-none absolute -top-6 z-10 flex justify-center text-[10px] font-semibold text-black"
          style={{
            left: `${userRankOverlayPosition}%`,
            transform: "translateX(-50%)",
          }}
        >
          <span className="px-1.5 py-0.5 rounded border border-black/60 bg-white shadow-sm">
            {userRank}
          </span>
        </div>
      )}
      {markers.map((marker, index) => {
        const clampedPosition = clampedMarkerPositions[index];
        const bottomOffsetPx = markerBottomOffsets[index];

        return (
          <div key={`${optionId}-${marker.key}`} className="pointer-events-none absolute inset-0">
            <div
              className="absolute -top-2 bottom-[-8px] border-l-[3px]"
              style={{
                left: `${clampedPosition}%`,
                borderColor: marker.color,
              }}
              aria-label={marker.label}
            />
            <div
              className="absolute text-[10px] font-semibold px-2 py-0.5 rounded shadow-sm border"
              style={{
                left: `${clampedPosition}%`,
                transform: "translateX(-50%)",
                backgroundColor: marker.labelBg,
                color: marker.labelText,
                borderColor: marker.color,
                bottom: `-${bottomOffsetPx}px`,
              }}
            >
              {marker.label}
            </div>
          </div>
        );
      })}
    </div>
  );
};
